import React, { useEffect, useState } from 'react';
import { Edit, Trash, CheckCircle, X } from 'lucide-react';
import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';
import { useDispatch, useSelector } from 'react-redux';
import { deleteblog } from '../../../FeatureRedux/blogs/DeleteBlogreducer';
import EditBlog from './editBlog';

const Blogtable = ({
  handleEdit,
  showSuccessModal,
  showUpdateModal,
  showDeleteModal,
  closeSuccessModal,
  closeUpdateModal,
  closeDeleteModal
}) => {
  const dispatch = useDispatch();
  const { loading, isError, isSucces, data } = useSelector((s) => s.getblog);

  const [blogs, setBlogs] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 5;

  useEffect(() => {
    dispatch(getblog());
  }, [dispatch]);

  useEffect(() => {
    if (isSucces) {
      setBlogs([...data?.blogs].reverse());
    }
  }, [isSucces, data]);

  const handleDelete = async (id) => {
    await dispatch(deleteblog(id));
    await dispatch(getblog())
  };

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
        <p className="font-bold">Error</p>
        <p>Error fetching blogs. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Modals */}
      {showSuccessModal && (
        <SuccessModal 
          title="Success!" 
          message="Blog added successfully." 
          onClose={closeSuccessModal} 
        />
      )}

      {showUpdateModal && (
        <SuccessModal 
          title="Success!" 
          message="Blog updated successfully." 
          onClose={closeUpdateModal} 
        />
      )}

      {showDeleteModal && (
        <SuccessModal 
          title="Success!" 
          message="Blog deleted successfully." 
          onClose={closeDeleteModal} 
        />
      )}

      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Read Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Link</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {Array.isArray(currentBlogs) && currentBlogs.length > 0 ? (
              currentBlogs.map((blog, index) => (
                <tr key={blog._id || index} className="hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={blog.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 hover:underline"
                    >
                      {blog.title?.length > 30 ? `${blog.title.substring(0, 30)}...` : blog.title}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{blog.readTime || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-300">{blog.date || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {blog.image ? (
                      <img
                        src={blog.image}
                        alt="Blog"
                        className="w-16 h-12 object-cover rounded-md border border-gray-600"
                      />
                    ) : (
                      <span className="text-gray-400">No image</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={blog.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 hover:underline"
                    >
                      {blog.url?.length > 20 ? `${blog.url.substring(0, 20)}...` : blog.url || '-'}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditIndex(index)}
                        className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(blog._id)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                  <div className="flex flex-col items-center justify-center">
                    <svg className="w-16 h-16 text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <p className="text-lg">No blogs found</p>
                    <p className="text-sm">Add a new blog to get started</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {blogs.length > blogsPerPage && (
        <div className="flex justify-between items-center mt-6 px-4 py-3 bg-gray-700 rounded-lg">
          <button
            onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-md ${currentPage === 1 ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-600'}`}
          >
            Previous
          </button>
          <div className="flex space-x-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => paginate(index + 1)}
                className={`px-4 py-2 rounded-md ${currentPage === index + 1 ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-600'}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button
            onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-md ${currentPage === totalPages ? 'text-gray-500 cursor-not-allowed' : 'text-gray-300 hover:bg-gray-600'}`}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Blog Modal */}
      {editIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl border border-gray-700">
            <div className="flex justify-between items-center bg-gray-700 px-6 py-4 border-b border-gray-600">
              <h3 className="text-xl font-semibold text-white">Edit Blog</h3>
              <button
                onClick={() => setEditIndex(null)}
                className="text-gray-300 hover:text-white p-1 rounded-full hover:bg-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <EditBlog
              blog={blogs[editIndex]}
              index={editIndex}
              closeModal={() => setEditIndex(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SuccessModal = ({ title, message, onClose }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50 p-4">
    <div className="bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full border border-gray-700">
      <div className="p-6 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
        <p className="text-gray-300 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

export default Blogtable;
// import React, { useEffect, useState } from 'react';
// import { Edit, Trash, CheckCircle, X } from 'lucide-react';
// import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';
// import { useDispatch, useSelector } from 'react-redux';
// import { deleteblog } from '../../../FeatureRedux/blogs/DeleteBlogreducer';
// import EditBlog from './editBlog';

// const Blogtable = ({
//   handleEdit,
//   showSuccessModal,
//   showUpdateModal,
//   showDeleteModal,
//   closeSuccessModal,
//   closeUpdateModal,
//   closeDeleteModal
// }) => {
//   const dispatch = useDispatch();
//   const { loading, isError, isSucces, data } = useSelector((s) => s.getblog);

//   const [blogs, setBlogs] = useState([]);
//   const [editIndex, setEditIndex] = useState(null); // <-- Modal control

//   useEffect(() => {
//     dispatch(getblog());
//   }, [dispatch]);

//   useEffect(() => {
//     if (isSucces) {
//       setBlogs([...data?.blogs].reverse());

//     }
//   }, [isSucces, data]);

  

//   const handleDelete = async (id) => {
//     await dispatch(deleteblog(id));
//     await dispatch(getblog())
//     // console.log(`object`)
//   };

//   if (loading) {
//     return <h1 className='text-red text-center'>Loading...</h1>;
//   }

//   if (isError) {
//     return <h1 className='text-red-600 text-center'>Error fetching blogs</h1>;
//   }

//   return (
//     <div className="p-6 bg-gray-900 shadow-lg rounded-xl" style={{
//       overflowY: 'scroll',
//       height: '100vh',
//       width: '100%',
//       scrollbarWidth: '1px',
//       msOverflowStyle: 'none'
//     }}>
//       {/* Modals */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog added successfully.</p>
//               <button
//                 onClick={closeSuccessModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {showUpdateModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog updated successfully.</p>
//               <button
//                 onClick={closeUpdateModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {showDeleteModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog deleted successfully.</p>
//               <button
//                 onClick={closeDeleteModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="overflow-x-auto">
//         <table className="min-w-full border border-gray-600 rounded-lg shadow-md">
//           <thead>
//             <tr className="bg-gray-900 text-white text-left">
//               <th className="px-6 py-2 border">Title</th>
//               <th className="px-6 py-2 border">Read Time</th>
//               <th className="px-6 py-2 border">Date</th>
//               <th className="px-6 py-2 border">Image</th>
//               <th className="px-6 py-2 border">Link</th>
//               <th className="px-6 py-2 border">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {Array.isArray(blogs) && blogs.length > 0 ? (
//               blogs.map((blog, index) => (
//                 <tr key={blog._id || index} className="border hover:bg-gray-800">
//                   <td className="px-6 py-1 border">
//                     <a
//                       href={blog.url || '#'}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-blue-600 hover:underline"
//                     >
//                       {blog.title?.length > 30 ? `${blog.title.substring(0, 30)}...` : blog.title}
//                     </a>
//                   </td>
//                   <td className="px-6 py-1 border text-white">{blog.readTime || '-'}</td>
//                   <td className="px-6 py-1 border text-white">{blog.date || '-'}</td>
//                   <td className="px-6 py-1 border">
//                     {blog.image ? (
//                       <img
//                         src={blog.image}
//                         alt="Blog"
//                         className="w-20 h-16 object-cover rounded-md border border-gray-600"
//                       />
//                     ) : (
//                       <span className="text-gray-400">No image</span>
//                     )}
//                   </td>
//                   <td className="px-6 py-1 border">
//                     <a
//                       href={blog.url || '#'}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-blue-600 hover:underline"
//                     >
//                       {blog.url?.length > 30 ? `${blog.url.substring(0, 30)}...` : blog.url || '-'}
//                     </a>
//                   </td>
//                   <td className="px-6 py-1 border">
//                     <div className="flex space-x-3">
//                       <button
//                         onClick={() => setEditIndex(index)} // Open modal
//                         className="text-green-500 hover:text-green-700"
//                       >
//                         <Edit className="w-5 h-5" />
//                       </button>
//                       <button
//                         onClick={() => handleDelete(blog._id)}
//                         className="text-red-500 hover:text-red-700"
//                       >
//                         <Trash className="w-5 h-5" />
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
//                   No blogs found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>

//       {/* Edit Blog Modal */}
//       {editIndex !== null && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full relative">
//             {/* Close button */}
//             <button
//               onClick={() => setEditIndex(null)}
//               className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
//             >
//               <X className="w-5 h-5" />
//             </button>

//             <EditBlog
//               blog={blogs[editIndex]}
//               index={editIndex}
//               closeModal={() => setEditIndex(null)}
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default Blogtable;



// import React, { useEffect, useState } from 'react';
// import { Edit, Trash, CheckCircle, X } from 'lucide-react';
// import { getblog } from '../../../FeatureRedux/blogs/GetBlogReducer';
// import { useDispatch, useSelector } from 'react-redux';
// import { deleteblog } from '../../../FeatureRedux/blogs/DeleteBlogreducer';
// import EditBlog from './editBlog';

// const Blogtable = ({
//   // blogs,
//   handleEdit,
//   // handleDelete,
//   showSuccessModal,
//   showUpdateModal,
//   showDeleteModal,
//   closeSuccessModal,
//   closeUpdateModal,
//   closeDeleteModal
// }) => {
//   const dispatch = useDispatch();
//   // const blogState = useSelector((state) => state.getblog || {});
//   const { loading, isError, isSucces, data } = useSelector((s) => s.getblog);

//   useEffect(() => {
//     dispatch(getblog());
//   }, [dispatch]);




//   const [blogs , setBlogs]= useState([])
//   useEffect(() => {
//   //  console.log( "This is is suvvees",isSucces , isError )

//     if (isSucces) {
      
//       setBlogs(data.blogs)
//     }
//   }, [isSucces])



//   const handleDelete = async (id) =>{
//     console.log("loyala  ",id)
//      await dispatch(deleteblog(id));
//   }

//   if (loading) {
//     return <h1 className='text-red text-center'>Loading...</h1>;
//   }
  

//   if (isError) {
//     return <h1 className='text-red-600 text-center'>Error fetching blogs</h1>;
//   }

//   return (
//     <div className="p-6 bg-gray-900 shadow-lg rounded-xl" style={{
//         overflowY: 'scroll',
//         height: '100vh',
//         width: '100%',
//         scrollbarWidth: '1px',
//         msOverflowStyle: 'none'
//       }}>
//       {/* Success Modal */}
//       {showSuccessModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog added successfully.</p>
//               <button
//                 onClick={closeSuccessModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Update Modal */}
//       {showUpdateModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog updated successfully.</p>
//               <button
//                 onClick={closeUpdateModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Modal */}
//       {showDeleteModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
//             <div className="flex flex-col items-center">
//               <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
//               <h3 className="text-lg font-medium text-gray-900 mb-2">Success!</h3>
//               <p className="text-gray-600 mb-4">Blog deleted successfully.</p>
//               <button
//                 onClick={closeDeleteModal}
//                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
//               >
//                 OK
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <div className="overflow-x-auto">
//         <table className="min-w-full border border-gray-600 rounded-lg shadow-md">
//           <thead>
//             <tr className="bg-gray-900 text-white text-left">
//               <th className="px-6 py-2 border">Title</th>
//               <th className="px-6 py-2 border">Read Time</th>
//               <th className="px-6 py-2 border">Date</th>
//               <th className="px-6 py-2 border">Image</th>
//               <th className="px-6 py-2 border">Link</th>
//               <th className="px-6 py-2 border">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {Array.isArray(blogs) && blogs.length > 0 ? (
//               blogs.map((blog, index) => (
//                 <tr key={blog._id || index} className="border hover:bg-gray-800">
//                   <td className="px-6 py-1 border">
//                     <a
//                       href={blog.url || '#'}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-blue-600 hover:underline"
//                     >
//                       {blog.title?.length > 30 ? `${blog.title.substring(0, 30)}...` : blog.title}
//                     </a>
//                   </td>
//                   <td className="px-6 py-1 border text-white">{blog.readTime || '-'}</td>
//                   <td className="px-6 py-1 border text-white">{blog.date || '-'}</td>
//                   <td className="px-6 py-1 border">
//                     {blog.image ? (
//                       <img
//                         src={blog.image}
//                         alt="Blog"
//                         className="w-20 h-16 object-cover rounded-md border border-gray-600"
//                       />
//                     ) : (
//                       <span className="text-gray-400">No image</span>
//                     )}
//                   </td>
//                   <td className="px-6 py-1 border">
//                     <a
//                       href={blog.url || '#'}
//                       target="_blank"
//                       rel="noopener noreferrer"
//                       className="text-blue-600 hover:underline"
//                     >
//                       {blog.url?.length > 30 ? `${blog.url.substring(0, 30)}...` : blog.url || '-'}
//                     </a>
//                   </td>
//                   <td className="px-6 py-1 border">
//                     <div className="flex space-x-3">
//                       <button
//                         onClick={() => handleEdit(index)}
//                         className="text-green-500 hover:text-green-700"
//                       >
//                         <Edit className="w-5 h-5" />
//                       </button>
//                       <button
//                          onClick={() => handleDelete(blog._id)}
//                         // onClick={()=>{console.log(blog._id)}}
//                         className="text-red-500 hover:text-red-700"
//                       >
//                         <Trash className="w-5 h-5" />
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               ))
//             ) : (
//               <tr>
//                 <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
//                   No blogs found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//       <EditBlog/>
//     </div>
//   );
// };

// export default Blogtable;

